import type {
  DeliverableStatus,
  DeliverableWritePayload,
  ProjectStatus,
  ProjectWritePayload,
} from '@/types/domain';

export interface WorkflowAction<T extends string> {
  value: T;
  label: string;
  danger?: boolean;
  submit?: boolean;
}

/*
  0916 Tony:「统一成一种」。

  原来项目有两个"在跑"的状态：「启动项目」写 `active`、再点「进入执行」写 `in_progress`。
  可**全系统没有一处把这两个值区分对待**——统计两个都数、
  `NOT_STARTED_STATUSES` 两个都不含、`statusText` 两个都翻成「进行中」。
  于是同一件事两种写法，模块中心的看板上画成两根柱子
  （「进行中（in_progress）14」和「进行中（active）1」）。

  现在启动直接到 `in_progress`。`active` 这一档保留：旧数据可能还停在上面，
  停在上面的人要能往下走。
*/
const projectTransitions: Record<ProjectStatus, WorkflowAction<ProjectStatus>[]> = {
  draft: [
    { value: 'in_progress', label: '启动项目' },
    { value: 'quotation', label: '转报价项目' },
    { value: 'cancelled', label: '取消', danger: true },
    { value: 'deleted', label: '删除', danger: true },
  ],
  quotation: [
    { value: 'in_progress', label: '转正式项目' },
    { value: 'cancelled', label: '取消', danger: true },
    { value: 'archived', label: '归档', danger: true },
    { value: 'deleted', label: '删除', danger: true },
  ],
  active: [
    { value: 'in_progress', label: '进入执行' },
    { value: 'paused', label: '暂停', danger: true },
    { value: 'closed', label: '关闭', danger: true },
    { value: 'cancelled', label: '取消', danger: true },
    { value: 'archived', label: '归档', danger: true },
    { value: 'deleted', label: '删除', danger: true },
  ],
  in_progress: [
    { value: 'paused', label: '暂停', danger: true },
    { value: 'closed', label: '关闭', danger: true },
    { value: 'cancelled', label: '取消', danger: true },
    { value: 'archived', label: '归档', danger: true },
    { value: 'deleted', label: '删除', danger: true },
  ],
  paused: [
    { value: 'active', label: '恢复为已启动' },
    { value: 'in_progress', label: '恢复执行' },
    { value: 'cancelled', label: '取消', danger: true },
    { value: 'archived', label: '归档', danger: true },
    { value: 'deleted', label: '删除', danger: true },
  ],
  closed: [
    { value: 'active', label: '重新打开' },
    { value: 'archived', label: '归档', danger: true },
    { value: 'deleted', label: '删除', danger: true },
  ],
  cancelled: [
    { value: 'draft', label: '恢复草稿' },
    { value: 'archived', label: '归档', danger: true },
    { value: 'deleted', label: '删除', danger: true },
  ],
  archived: [
    { value: 'active', label: '取消归档' },
    { value: 'deleted', label: '删除', danger: true },
  ],
  deleted: [{ value: 'archived', label: '恢复到归档' }],
};

const deliverableTransitions: Record<
  DeliverableStatus,
  WorkflowAction<DeliverableStatus>[]
> = {
  draft: [
    { value: 'submitted', label: '提交审批', submit: true },
    { value: 'cancelled', label: '取消', danger: true },
  ],
  pending: [
    { value: 'submitted', label: '提交审批', submit: true },
    { value: 'cancelled', label: '取消', danger: true },
  ],
  submitted: [
    { value: 'approved', label: '批准' },
    { value: 'rejected', label: '驳回', danger: true },
    { value: 'cancelled', label: '取消', danger: true },
  ],
  approved: [
    { value: 'completed', label: '确认完成' },
    { value: 'archived', label: '归档', danger: true },
  ],
  rejected: [
    { value: 'submitted', label: '重新提交', submit: true },
    { value: 'cancelled', label: '取消', danger: true },
  ],
  completed: [{ value: 'archived', label: '归档', danger: true }],
  cancelled: [{ value: 'pending', label: '恢复待处理' }],
  archived: [],
};

const legacyProjectStatuses: Record<string, ProjectStatus> = {
  草稿: 'draft',
  报价: 'quotation',
  已激活: 'active',
  进行中: 'in_progress',
  已暂停: 'paused',
  已关闭: 'closed',
  已取消: 'cancelled',
  已归档: 'archived',
  已删除: 'deleted',
};

function nullableText(value: string | null | undefined): string | null {
  const normalized = value?.trim();
  return normalized ? normalized : null;
}

export function normalizedProjectStatus(status: string | null | undefined): ProjectStatus | null {
  if (!status) return null;
  return legacyProjectStatuses[status] ?? (
    Object.prototype.hasOwnProperty.call(projectTransitions, status)
      ? status as ProjectStatus
      : null
  );
}

export function projectStatusActions(
  status: string | null | undefined,
): WorkflowAction<ProjectStatus>[] {
  const normalized = normalizedProjectStatus(status);
  return normalized ? projectTransitions[normalized] : [];
}

export function deliverableStatusActions(
  status: DeliverableStatus,
): WorkflowAction<DeliverableStatus>[] {
  return deliverableTransitions[status] ?? [];
}

export function buildProjectWritePayload(
  form: Record<string, string>,
): ProjectWritePayload {
  return {
    projectNo: form.projectNo?.trim() ?? '',
    projectName: form.projectName?.trim() ?? '',
    customer: nullableText(form.customer),
    projectType: nullableText(form.projectType),
    priority: nullableText(form.priority),
    startDate: nullableText(form.startDate),
    endDate: nullableText(form.endDate),
    description: nullableText(form.description),
  };
}

export function optionalPositiveInteger(value: string): number | null {
  const normalized = value.trim();
  if (!normalized) return null;
  if (!/^[1-9]\d*$/.test(normalized)) return null;
  return Number(normalized);
}

export function deliverableDueAt(value: string): string | null {
  if (!value.trim()) return null;
  return new Date(`${value}T23:59:59+08:00`).toISOString();
}

export function buildDeliverableWritePayload(form: {
  nodeId: string;
  code: string;
  title: string;
  ownerUserId: string;
  ownerName: string;
  dueDate: string;
  documentId: string;
}): DeliverableWritePayload {
  return {
    nodeId: optionalPositiveInteger(form.nodeId),
    code: nullableText(form.code),
    title: form.title.trim(),
    ownerUserId: optionalPositiveInteger(form.ownerUserId),
    ownerName: nullableText(form.ownerName),
    dueAt: deliverableDueAt(form.dueDate),
    documentId: optionalPositiveInteger(form.documentId),
  };
}
